import type {BannerDataText} from '@core-client/ads/banner/data/BannerDataText';
import type {IBannerData} from '@core-client/ads/banner/data/IBannerData';
import {BannerCfg} from '../config/BannerCfg';
import {AdfBannerTransport} from '../transport/AdfBannerTransport';
import {BannerTransport} from '../transport/BannerTransport';
import type {IBannerRequestResult} from '../transport/IBannerTransport';
import {NavigationTrigger} from '../trigger/NavigationTrigger';
import {TimeoutTrigger} from '../trigger/TimeoutTrigger';
import {createLog, log, parseJson} from '../util/BannerCommon';
import {BannerConstant} from '../util/BannerConstant';
import {getBannerStatLogger, getErrorOperationWithSuffix} from '../util/BannerStat';
import {ValueProvider} from '../util/ValueProvider';
import {detect as adbWhitelistDetect} from '../util/wld';
import {BannerProxy} from '../variant/BannerProxy';
import type {IBanner} from '../variant/IBanner';
import {ISimpleBannerOptions, SimpleBanner} from '../variant/SimpleBanner';
import {BannerView, type IBannerViewOptions} from '../view/BannerView';
import {BannerModel} from '../BannerModel';

import {HookBannerParams, type IAddRequestParams} from './HookBannerParams';
import type {IBannerJson} from './IBannerJson';

export interface IBannerHookSettings<D extends IBannerJson> {
    /** Название баннера. */
    bannerName: string;
    /** Параметры баннера. */
    bannerParams?: D;
    /** Надо ли очищать элемент-контейнер от атрибутов с данными. */
    cleanElement?: boolean;
    /** Элемент-контейнер для баннера. */
    element: HTMLElement;
}

export class BaseBannerHook<D extends IBannerJson> {
    element: HTMLElement;
    name: string;
    log: (...args: (PropertyKey | object)[]) => void;
    position: string = '';
    banner: IBanner | null = null;
    bannerCfg: BannerCfg;
    bannerParams: D | null;
    bannerRequestResult: IBannerRequestResult;
    cleanElement: boolean;
    visible: boolean = true;
    params: HookBannerParams | null = null;
    showBannerTimeout: number;
    initResult?: Promise<void>;

    constructor(settings: IBannerHookSettings<D>) {
        this.element = settings.element;
        this.bannerCfg = new BannerCfg();
        this.bannerParams = settings.bannerParams || null;
        this.cleanElement = settings.cleanElement || false;
        this.name = settings.bannerName;
        this.log = createLog(`BaseBannerHook[${this.name}]`);
        this.showBannerTimeout = 500;
    }

    needCleanElement() {
        return this.cleanElement;
    }

    activate() {
        this.log('activate');

        if (this.showBannerOnActivate()) {
            this.showBanner();
        } else {
            this.init();
        }
    }

    init(): Promise<void> {
        if (!this.initResult) {
            this.initResult = adbWhitelistDetect()
                .then(
                    () => true,
                    () => false
                )
                .then(adbWhitelistDetected => {
                    this.params = this.readBannerParams(adbWhitelistDetected);
                    if (this.needCleanElement()) {
                        // чтобы не палиться перед adblock
                        this.element.removeAttribute('data-clean');
                        this.element.removeAttribute('data-hook-id');
                        this.element.removeAttribute('data-module');
                        this.element.removeAttribute('data-params');
                    }
                })
                .then(() => {
                    this.log('params', this.params!);
                });
        }
        return this.initResult;
    }

    showBanner(): Promise<void> {
        return this.init().then(() => this.doShowBanner());
    }

    doShowBanner(): Promise<void> {
        this.clearBannerContent();
        if (this.params !== null) {
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    try {
                        const el = this.element;
                        const isActive = el !== null;
                        if (isActive) {
                            this.banner = new BannerProxy({
                                createDelegate: () => {
                                    return this.createBannerDelegate(this.params!, el);
                                },
                                key: () => 'banner',
                                slot: this.position
                            });
                            this.banner.init(this.visible);
                            this.banner.show();
                            this.log('banner', this.banner);
                            resolve();
                        }
                    } catch (exception) {
                        const ex = exception as Error;
                        this.log('error', ex);
                        getBannerStatLogger().errorEx(
                            ex,
                            `BannerHook-${this.name}`,
                            "init_view_",
                            getErrorOperationWithSuffix(this.position)
                        );
                        reject(ex);
                    }
                }, this.showBannerTimeout);
            });
        } else {
            return Promise.reject();
        }
    }

    deactivate() {
        if (this.banner !== null) {
            this.banner.destroy();
            this.banner = null;
        }
    }

    showBannerOnActivate() {
        return true;
    }

    handleBannerRender() {
    }

    logEmptyBanner(): void {
    }

    createBannerDelegate(params: HookBannerParams, container: HTMLElement): IBanner {
        const slot = ValueProvider.create<string>(params.getPosition());
        return this.buildInlineBanner(container, params, slot, this.name);
    }

    prepareBannerViewOptions(options: IBannerViewOptions): IBannerViewOptions {
        return options;
    }

    buildInlineBanner(container: HTMLElement, params: HookBannerParams, slot: ValueProvider<string>, bannerName: string): SimpleBanner {
        const bannerTransport = params.isUseAdfTransport()
            ? new AdfBannerTransport(this.bannerCfg)
            : new BannerTransport(this.bannerCfg);
        const model = new BannerModel(slot, params, bannerTransport);
        const view = new BannerView(this.prepareBannerViewOptions({
            container: container,
            slot: slot,
            adfTransport: params.isUseAdfTransport(),
            inline: true,
            onRender: () => {
                this.handleBannerRender();
            },
            onError: () => {
                this.logEmptyBanner();
            }
        }));

        const banner = this.createBanner(view, model);
        banner.setInLayer(params.getInLayer());

        const timeoutConfig = this.bannerCfg.timeoutCfg;

        const refreshOnNavigate = new ValueProvider<boolean>(() => timeoutConfig.getTransition(bannerName));

        const refreshTimeout = new ValueProvider<number>(() => timeoutConfig.getTimeout(bannerName));

        // добавляем триггеры для обновления баннера
        // при навигации
        banner.attachRefreshTrigger(new NavigationTrigger(refreshOnNavigate), "navi_hook");
        // по таймауту
        banner.attachRefreshTrigger(new TimeoutTrigger(refreshTimeout), "timeout_hook");

        return banner;
    }

    prepareBannerOptions(options: ISimpleBannerOptions): ISimpleBannerOptions {
        return options;
    }

    protected handleBannerResponse = (bannerData: IBannerData) => {
        this.bannerRequestResult = bannerData;
    };

    createBanner(view: BannerView, model: BannerModel): SimpleBanner {
        return new SimpleBanner(this.prepareBannerOptions({
            view: view,
            model: model,
            onBeforeHandleResponse: this.handleBannerResponse
        }));
    }

    getBannerJsonParams(): D | null {
        if (this.bannerParams) {
            return this.bannerParams;
        }
        let params = this.element.getAttribute("data-params");
        if (params) {
            params = params.trim();
        }
        if (params) {
            return parseJson<D>(params);
        }
        return null;
    }

    readBannerParams(adbWhitelistDetected: boolean): HookBannerParams | null {
        const {bannerCfg} = this;
        const json = this.getBannerJsonParams() as Record<string, unknown>;
        log('bannerParams', json);
        if (json === null) {
            return null;
        }
        const withUniqueId = json[BannerConstant.WITH_UNIQUE_ID] as boolean;
        const withPersonalData = json[BannerConstant.WITH_PERSONAL_DATA] as boolean;
        const withLastApps = json[BannerConstant.WITH_LAST_APPS] as boolean;
        const position = String(json[BannerConstant.POSITION_KEY] || json.slot || '');
        const companyOwner = !!json[BannerConstant.PERSONAL_DATA_IS_COMPANYOWNER];
        const withSiteZone = json[BannerConstant.WITH_SITE_ZONE_BANNER_PARAM] as boolean;

        const params = new HookBannerParams(withUniqueId, withPersonalData, withLastApps, position, companyOwner, withSiteZone);
        params.setAdblockWhitelist(adbWhitelistDetected);

        if (json.grname) {
            params.setAuxGroupParameters(
                json.grname as string,
                json.grdesc as string,
                json.grpic as string
            );
        }

        bannerCfg.callbacksEnabled = Boolean(json.logStat);
        if (json.place && typeof json.place === 'string') {
            bannerCfg.place = json.place;
        }

        this.position = position;
        this.visible = json.visible as typeof this.visible;
        this.readAdditionalParams(json as D, params);

        if (json[BannerConstant.ADD_REQUEST_PARAMS]) {
            params.setAddRequestParams(json[BannerConstant.ADD_REQUEST_PARAMS] as IAddRequestParams);
        }
        if (json[BannerConstant.APPS_BANNERS_WITH_CUSTOM_TITLE]) {
            params.setUseAppsCustomTitle(true);
        }
        if (json[BannerConstant.APPS_BANNERS_APP_SHORT_NAME]) {
            params.setAppShortName(json[BannerConstant.APPS_BANNERS_APP_SHORT_NAME] as string);
        }

        params.setDelay((json[BannerConstant.EXPR_DELAY] || 50) as number);
        params.setInLayer((json[BannerConstant.IN_LAYER] || false) as boolean);
        if (json.passWidth) {
            const element = this.element;
            const width = BaseBannerHook.getInnerWidth(element);
            params.setWidth(width);
        }
        if (json.passHeight) {
            params.setWithViewportHeight(true);
        }
        const siteZone = json[BannerConstant.SITE_ZONE_BANNER_PARAM];
        if (siteZone) {
            params.setSiteZone(siteZone as string);
        }
        const sitename = json[BannerConstant.SITENAME];
        if (sitename) {
            params.setSitename(sitename as string);
        }
        params.contentId = json[BannerConstant.CONTENT_ID] as string;

        const useAdfTransport = json[BannerConstant.ADF_TRANSPORT_PARAM] || false;
        params.setUseAdfTransport(useAdfTransport as boolean);

        params.setBannerCfg(bannerCfg);

        const withDarkThemeParam = json[BannerConstant.WITH_DARK_THEME_PARAM] || false;
        params.setWithDarkThemeParam(withDarkThemeParam as boolean);

        const withFpidParam = json[BannerConstant.WITH_FPID_PARAM] || false;
        params.setWithFpidParam(withFpidParam as boolean);

        return params;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected readAdditionalParams(json: D, params: HookBannerParams) {

    }

    clearBannerContent() {
        this.element.innerHTML = '';
    }

    static getInnerWidth(el: HTMLElement): number {
        const computedStyle = window.getComputedStyle(el, null);
        const paddingLeft = parseInt(computedStyle.paddingLeft, 10);
        const paddingRight = parseInt(computedStyle.paddingRight, 10);
        return el.clientWidth - paddingLeft - paddingRight;
    }
}
